<template>
  <b-container fluid>
    <ResourceList
      can-delete create-to="dash.customers.create" resource="customer" soft-deletes title="Recently Active" :has-filters="true"
      :data="data" :is-deleting="isDeleting" :is-loading="isLoading" :pagination="pagination"
      :search-query="$route.query.query" @delete-many="onDeleteMany" @paginate="onPaginate"
      @search="onSearch">

      <template #filters>
        <b-row>
          <b-col cols="12">
            <b-form-group label="Active Within">
              <b-form-select v-model="days" @change="onFilterChange">
                <b-form-select-option :value="7">Last 7 days</b-form-select-option>
                <b-form-select-option :value="14">Last 14 days</b-form-select-option>
                <b-form-select-option :value="30">Last 30 days</b-form-select-option>
                <b-form-select-option :value="60">Last 60 days</b-form-select-option>
                <b-form-select-option :value="90">Last 90 days</b-form-select-option>
                <b-form-select-option :value="180">Last 180 days</b-form-select-option>
                <b-form-select-option :value="365">Last 365 days</b-form-select-option>
              </b-form-select>
            </b-form-group>
          </b-col>
          <b-col cols="12">
            <b-form-group label="Customer Status">
              <b-form-select v-model="customerStatus" @change="onFilterChange">
                <b-form-select-option :value="null">All (excl. BOOM &amp; tickets)</b-form-select-option>
                <b-form-select-option v-for="status in filterableStatuses" :key="status" :value="status">{{ status }}</b-form-select-option>
              </b-form-select>
            </b-form-group>
          </b-col>
          <b-col cols="12">
            <b-form-group label="Showroom">
              <b-form-select v-model="showroomId" @change="onFilterChange">
                <b-form-select-option :value="null">All</b-form-select-option>
                <b-form-select-option v-for="showroom in showrooms" :key="showroom.id" :value="showroom.id">{{ showroom.name }}</b-form-select-option>
              </b-form-select>
            </b-form-group>
          </b-col>
          <b-col cols="12">
            <b-form-group label="Interested In">
              <b-form-input
                v-model="interestedIn"
                placeholder="e.g. Cayman"
                @input="onInterestedInInput"
              />
            </b-form-group>
          </b-col>
        </b-row>
      </template>

      <template #listItem="{ item }">
        <div class="d-flex flex-column flex-fill">
          <div class="d-flex flex-row flex-fill">
            <p class="h5 flex-fill mb-1 text-primary"><router-link class="text-dark" :to="{name: 'dash.customers.view', params: { id: item.id }}">{{ [item.forename, item.surname].join(' ') }}</router-link></p>
            <p class="align-self-end mb-1 text-muted"><FriendlyDate :date="item.created_at" /></p>
          </div>
          <div class="d-flex flex-row flex-fill mb-0 mb-md-1">
            <div class="d-flex flex-column flex-fill">
              <p class="mb-0">{{ item.email }}</p>
              <small v-if="item.tickets_count" class="mb-0 text-danger"><fa-icon class="mr-1" icon="flag"/>This customer has a ticket.</small>
              <small class="mb-0 text-muted" v-if="item.user_id">Handled By: {{ item.user.name }}</small>
            </div>
            <p class="mb-0 text-muted" v-if="item.status">{{ item.status }}</p>
          </div>
          <div class="d-none d-md-flex flex-row">
            <b-button class="mr-1 mb-0" size="sm" variant="primary" v-b-popover.hover.bottom="'View more information about this customer.'" :to="{name: 'dash.customers.view', params: { id: item.id }}"><b-icon icon="person" /> View</b-button>
            
            <b-dropdown size="sm" :id="`call-drop-down-${ item.id }`" v-if="item.mobile && item.landline">
              <template #button-content>
                <b-icon icon="telephone-outbound" /> Call
              </template>
              <b-dropdown-item @click="initiateCall(item.mobile)">Mobile</b-dropdown-item>
              <b-dropdown-item @click="initiateCall(item.landline)">Landline</b-dropdown-item>
            </b-dropdown>

            <b-button class="mr-1 mb-0" size="sm" variant="secondary" v-if="!item.landline && item.mobile" @click="initiateCall(item.mobile)" v-b-popover.hover.bottom="'Uses your desktop phone to initiate a call to the customer.'"><b-icon icon="telephone-outbound" /> Call Mobile</b-button>
            <b-button class="mr-1 mb-0" size="sm" variant="secondary" v-if="!item.mobile && item.landline" @click="initiateCall(item.landline)" v-b-popover.hover.bottom="'Uses your desktop phone to initiate a call to the customer.'"><b-icon icon="telephone-outbound" /> Call</b-button>

            <p class="align-self-center ml-2 mb-0" v-if="item.last_contacted_at">Last contacted <FriendlyDate class="align-self-center d-inline text-muted" :date="item.last_contacted_at" />.</p>
          </div>
          
          <b-badge class="mt-2" pill variant="success" v-if="item.last_active_at && item.last_active_at > item.created_at"><FriendlyDate :date="item.last_active_at" /></b-badge>
          
        </div>
      </template>

    </ResourceList>
  </b-container>
</template>

<script>
import FriendlyDate from '../../components/FriendlyDate'
import ResourceList from '../../components/ResourceList'
import currentUser from '../../mixins/currentUser'
import { mapActions, mapGetters } from 'vuex'

export default {
  mixins: [currentUser],
  created () {
    if (this.$route.query.days) {
      this.days = parseInt(this.$route.query.days, 10) || 30
    }
    if (this.$route.query.status) {
      this.customerStatus = this.$route.query.status
    }
    if (this.$route.query.showroomId) {
      this.showroomId = parseInt(this.$route.query.showroomId, 10) || null
    }
    if (this.$route.query.interestedIn) {
      this.interestedIn = this.$route.query.interestedIn
    }
    this.getStatuses().then(response => {
      this.statuses = response.data
    })
    window.axios.get('/showrooms', { params: { page: 1 } }).then(response => {
      this.showrooms = response.data.data || []
    })
    this.fetchManyRecentlyActive(this.fetchParams())
  },
  beforeDestroy () {
    clearTimeout(this.interestedInDebouncer)
  },
  components: {
    FriendlyDate,
    ResourceList
  },
  data: () => ({
    days: 30,
    customerStatus: null,
    statuses: [],
    showrooms: [],
    showroomId: null,
    interestedIn: '',
    interestedInDebouncer: null,
  }),
  computed: {
    ...mapGetters('customers', ['data', 'isDeleting', 'isLoading', 'pagination']),
    filterableStatuses () {
      return this.statuses.filter(status => !['BOOM', 'BOOM Finance'].includes(status))
    }
  },
  mounted() {
    this.$root.$on('call-connected', call => {
      if(call.customer_id) this.updateLastContactedAt(call.customer_id)
    })
  },
  methods: {
    ...mapActions('customers', ['deleteMany', 'fetchManyRecentlyActive', 'updateLastContactedAt', 'getStatuses']),
    fetchParams (overrides = {}) {
      return {
        ...this.$route.query,
        days: this.days,
        customerStatus: this.customerStatus,
        showroomId: this.showroomId,
        interestedIn: this.interestedIn || null,
        ...overrides
      }
    },
    async onDeleteMany ({ ids, modalId }) {
      await this.deleteMany({ ids })
      this.$bvModal.hide(modalId)
      await this.fetchManyRecentlyActive(this.fetchParams())
    },
    async onPaginate (page) {
      if (this.$route.query.page !== page) {
        this.$router.push({ name: this.$route.name, query: { ...this.$route.query, page, days: this.days } })
        await this.fetchManyRecentlyActive(this.fetchParams({ page }))
      }
    },
    async onSearch (query) {
      if (this.$route.query.query !== query) {
        this.$router.push({ name: this.$route.name, query: { ...this.$route.query, query, days: this.days } })
        await this.fetchManyRecentlyActive(this.fetchParams({ query }))
      }
    },
    onInterestedInInput () {
      clearTimeout(this.interestedInDebouncer)
      this.interestedInDebouncer = setTimeout(() => {
        this.onFilterChange()
      }, 350)
    },
    async onFilterChange () {
      this.$router.push({
        name: this.$route.name,
        query: {
          ...this.$route.query,
          days: this.days,
          status: this.customerStatus,
          showroomId: this.showroomId,
          interestedIn: this.interestedIn || undefined,
          page: 1
        }
      })
      await this.fetchManyRecentlyActive(this.fetchParams({ page: 1 }))
    },
    initiateCall(number) { this.$root.$emit('initiate-call', number); }
  }
}
</script>

<style>

</style>
